import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ModalDrawerControllerProps, Spinner } from '@economic/gaudi';
import { User } from '@economic/react-app';

import ConsentStep from './ConsentStep';
import CompanyDataStep from './CompanyDataStep';
import UserDataStep from './UserDataStep';
import PaymentDataStep from './PaymentDataStep/PaymentDataStep';
import InformationSentStep from './InformationSentStep/InformationSentStep';
import { SIGNUP_STEPS } from '.';

import track from '../../../../../../shared/util/utilities/mixpanel';
import { t, tBB } from '../../../../../../shared/components/l10n/l10n';
import internalApiClient from '../../../../../../shared/util/api/internalApiClient';
import { Journal, OnboardingStatus } from '../../../../../../../types/internal-api';
import { SmartPayCustomerType } from '../../types';
import ExternalLink from '../../../../../../shared/components/ExternalLink';

import NovemberFirstLogo from '../../assets/N1_Logo.svg';
import './style.css';

interface Props {
    loadingTexts: boolean;
    isDataLoading: boolean;
    hide: () => void;
    onModalClose: (hide?: () => void) => void;
    currentStepIndex: number;
    setCurrentStepIndex: Dispatch<SetStateAction<number>>;
    onboardingData: OnboardingStatus;
    userData: User;
    journals: Journal[];
    paymentAccounts: [];
    feeAccounts: [];
    refetchState?: () => void;
    inMarket?: boolean;
    novemberFirstCustomerData: any;
    drawerProps: ModalDrawerControllerProps;
    preSelectedJournalName: string | undefined;
}

const SignupModalContent = ({
    loadingTexts,
    isDataLoading,
    hide,
    onModalClose,
    currentStepIndex,
    setCurrentStepIndex,
    onboardingData,
    userData,
    journals,
    paymentAccounts,
    feeAccounts,
    refetchState,
    inMarket,
    novemberFirstCustomerData,
    drawerProps,
    preSelectedJournalName,
}: Props) => {
    const [isSendingOnboardingInfo, setIsSendingOnboardingInfo] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const customerType: SmartPayCustomerType = novemberFirstCustomerData?.customerType
        ? novemberFirstCustomerData.customerType
        : SmartPayCustomerType.NewCustomer;
    const cvrAlreadyOnboarded: boolean = novemberFirstCustomerData?.cvrAlreadyOnboarded ? true : false;
    const anotherOnboardingAlreadyInProgress: boolean = novemberFirstCustomerData?.onboardingAlreadyInProgress ? true : false;
    const allCustomersOnboarded: boolean = novemberFirstCustomerData?.allCustomersOnboarded ? true : false;
    const novemberFirstCustomerNumberList: string[] = novemberFirstCustomerData?.customerNumbers
        ? novemberFirstCustomerData.customerNumbers
        : [];

    // user data
    const firstNameState = useState(userData.firstName);
    const lastNameState = useState(userData.lastName);
    const emailState = useState(userData.email);
    const phoneState = useState('');

    //payment data
    const bankAccountState = useState<number>();
    const [journalId, setJournalId] = useState<number>();
    const feeAccountState = useState<number>();
    const [selectedCustomerNumber, setSelectedCustomerNumber] = useState<string>();

    const stepsArray = Object.keys(SIGNUP_STEPS);

    useEffect(() => {
        if (stepsArray[currentStepIndex] === SIGNUP_STEPS.INFORMATION_SENT) postOnboardingInfo();
    }, [currentStepIndex]);

    useEffect(() => {
        // preselect recently added journal
        if (!preSelectedJournalName) return;

        const preSelectedJournal: Journal | undefined = journals.find((j: Journal) => j.name === preSelectedJournalName);

        if (!preSelectedJournal) return;
        setJournalId(preSelectedJournal.id);
    }, [preSelectedJournalName]);

    const onNextStep = () => {
        setCurrentStepIndex(currentStepIndex + 1);
    };

    const onBackStep = () => {
        setCurrentStepIndex(currentStepIndex - 1);
    };

    const getAndSetErrorMessage = (errorCode: string) => {
        let errorMessage = t(errorCode);
        const doesErrorMessageHaveTranslation = errorMessage !== errorCode;

        if (doesErrorMessageHaveTranslation) {
            if (errorCode === 'SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_ACCOUNT_NUMBER_LENGTH_INVALID') {
                track('smartpay', 'signup.modal.error-bankaccount-length');
                errorMessage = (
                    <>
                        <p>{errorMessage}</p>
                        <p>
                            {tBB(`SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_ACCOUNT_NUMBER_LENGTH_INVALID_CTA`, {
                                __LINK: content => (
                                    <ExternalLink href="/settings/company/bank-accounts" target="_blank">
                                        {content}
                                    </ExternalLink>
                                ),
                            })}
                        </p>
                    </>
                );
            } else if (errorCode === 'SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_BANK_NOT_SUPPORTED') {
                track('smartpay', 'signup.modal.error-bank-notsupported');
            }
            setErrorMessage(errorMessage);
        } else {
            setErrorMessage(t`SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_DEFAULT_MESSAGE`);
            track('smartpay', 'signup.modal.error-generic');
        }
    };

    const prepareCVRNumber = (cvr: string) => {
        if (cvr.match(/^([dD][kK])(?:\d{8})$/) !== null) {
            // remove country code from the CVR
            const regex = /^([dD][kK])/;
            return cvr.replace(regex, '');
        }

        if (cvr.match(/^(\d{8})$/) !== null) return cvr;

        return null;
    };

    const postOnboardingInfo = async () => {
        setIsSendingOnboardingInfo(true);

        try {
            const onboardingPayload = {
                CustomerType: customerType,
                Daybook: journalId,
                PaymentAccount: bankAccountState[0],
                FeeAccount: feeAccountState[0],
                Cvr: prepareCVRNumber(onboardingData.companyData.cvr),
                Telephone: phoneState[0],
                Email: emailState[0],
                CustomerNumber: selectedCustomerNumber,
                FirstName: firstNameState[0],
                LastName: lastNameState[0],
            };

            const res = await internalApiClient.post('/smartpay/initiateOnboarding', onboardingPayload);

            if (res.status === 201) {
                setIsSendingOnboardingInfo(false);
            }
        } catch (e) {
            setIsSendingOnboardingInfo(false);

            getAndSetErrorMessage(e.response.data.message);
        }
    };

    return (
        <>
            {loadingTexts ? (
                <div className="flex flex-col items-center justify-center h-128">
                    <Spinner />
                </div>
            ) : stepsArray[currentStepIndex] === SIGNUP_STEPS.CONSENT ? (
                <ConsentStep
                    totalSteps={stepsArray.length}
                    isDataLoading={isDataLoading}
                    onModalClose={() => onModalClose(hide)}
                    next={onNextStep}
                    anotherOnboardingAlreadyInProgress={anotherOnboardingAlreadyInProgress}
                    cvrAlreadyOnboarded={cvrAlreadyOnboarded}
                    allCustomersOnboarded={allCustomersOnboarded}
                />
            ) : stepsArray[currentStepIndex] === SIGNUP_STEPS.COMPANY_DATA ? (
                <CompanyDataStep
                    totalSteps={stepsArray.length}
                    next={onNextStep}
                    back={onBackStep}
                    companyData={onboardingData.companyData}
                />
            ) : stepsArray[currentStepIndex] === SIGNUP_STEPS.USER_DATA ? (
                <UserDataStep
                    totalSteps={stepsArray.length}
                    next={onNextStep}
                    back={onBackStep}
                    userDataStates={{
                        firstNameState,
                        lastNameState,
                        emailState,
                        phoneState,
                    }}
                />
            ) : stepsArray[currentStepIndex] === SIGNUP_STEPS.PAYMENT_DATA ? (
                <PaymentDataStep
                    totalSteps={stepsArray.length}
                    next={onNextStep}
                    back={onBackStep}
                    journals={journals}
                    journalsState={[journalId, setJournalId]}
                    bankAccounts={paymentAccounts}
                    bankAccountState={bankAccountState}
                    feeAccounts={feeAccounts}
                    feeAccountState={feeAccountState}
                    customerNumberState={[selectedCustomerNumber, setSelectedCustomerNumber]}
                    customerNumberOptions={novemberFirstCustomerNumberList}
                    customerType={customerType}
                    allCustomersOnboarded={allCustomersOnboarded}
                    onModalClose={() => onModalClose(hide)}
                    drawerProps={drawerProps}
                />
            ) : stepsArray[currentStepIndex] === SIGNUP_STEPS.INFORMATION_SENT ? (
                isSendingOnboardingInfo ? (
                    <div className="flex justify-center items-center h-40 w-full " style={{ height: '500px' }}>
                        <Spinner delay={0} />
                    </div>
                ) : (
                    <InformationSentStep
                        errorMessage={errorMessage}
                        setErrorMessage={setErrorMessage}
                        back={onBackStep}
                        totalSteps={stepsArray.length}
                        onModalClose={() => onModalClose(hide)}
                        refetchState={refetchState}
                        inMarket={inMarket}
                        email={emailState[0]}
                        customerType={customerType}
                    />
                )
            ) : (
                onModalClose(hide)
            )}

            <div className="mt-8">
                <div className="absolute left-0 w-full border-grey-dark" style={{ borderTopWidth: '3px', bottom: '58px' }}></div>
                <div className="flex items-center">
                    {t`SMARTPAY_ONBOARDING_MODAL_DELIVERED_BY`}
                    <img src={NovemberFirstLogo} alt="November First logo" className="h-3 ml-2" />
                </div>
            </div>
        </>
    );
};

export default SignupModalContent;
